import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { selectCurrentLocation } from 'redux/actions/location';
import { fetchLocationFeatures } from 'components/customization-page-container/customization-page.queries';
import { LocationFeatureV2 } from 'models/location-feature.model';
import { WIE_METRIC_DEFINITIONS } from '../wie-metric-definitions';
import { WieMetricEvalContext } from '../wie-metrics.types';
import {
  ChecklistMetricItem,
  ChecklistMetricStatus,
} from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import {
  initializeChecklistMetrics,
  blockChecklistMetrics,
  evaluateMetricDefinition,
} from '../../handoff-checklist-v2/handoff-checklist-v2.helpers';

const createInitialMetrics = (): ChecklistMetricItem[] =>
  WIE_METRIC_DEFINITIONS.map((def) => ({
    id: def.id,
    name: def.name,
    isRequired: def.isRequired,
    allowExceptions: def.allowExceptions,
    status: ChecklistMetricStatus.PENDING,
    isLoading: true,
    criteria: def.criteria.map((c) => ({
      description: c.description,
      status: ChecklistMetricStatus.PENDING,
      exceptionType: c.exceptionType,
      exceptionOptions: c.exceptionOptions,
    })),
  }));

export const useWieHandoffMetrics = (handoffId?: string) => {
  const [metrics, setMetrics] = useState<ChecklistMetricItem[]>(createInitialMetrics);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const currentLocation = useSelector(selectCurrentLocation);
  const locationId = currentLocation?.LocationID ?? '';
  const queryClient = useQueryClient();

  const generationRef = useRef<number>(0);
  const currentMetricsRef = useRef<ChecklistMetricItem[]>([]);

  // Keep ref up to date
  useEffect(() => {
    currentMetricsRef.current = metrics;
  }, [metrics]);

  const fetchMetrics = useCallback(async () => {
    const generation = ++generationRef.current;
    setIsLoading(true);
    setError(null);

    try {
      // Keep the checklist pending until the flags are freshly loaded so stale
      // passing results can't enable Submit.
      const pendingMetrics = initializeChecklistMetrics(
        WIE_METRIC_DEFINITIONS,
        { locationId, customizationFeatures: [] },
        currentMetricsRef.current
      );
      setMetrics(pendingMetrics);
      currentMetricsRef.current = pendingMetrics;

      let customizationFeatures: LocationFeatureV2[] = [];
      if (locationId) {
        try {
          customizationFeatures = await fetchLocationFeatures(queryClient, locationId, {
            force: true,
          });
        } catch {
          if (generation === generationRef.current) {
            setMetrics(
              blockChecklistMetrics(
                pendingMetrics,
                'Failed to refresh customization flags'
              )
            );
            setError('Failed to refresh customization flags');
            setIsLoading(false);
          }
          return;
        }
      }
      if (generation !== generationRef.current) return;

      const ctx: WieMetricEvalContext = { locationId, customizationFeatures };
      setIsLoading(false);

      // Staggered evaluation
      WIE_METRIC_DEFINITIONS.forEach(async (def) => {
        try {
          const existingMetric = currentMetricsRef.current.find((m) => m.id === def.id);
          const { overallStatus, evaluatedCriteria } = await evaluateMetricDefinition(
            def,
            ctx,
            existingMetric
          );

          if (generation === generationRef.current) {
            setMetrics((prev) =>
              prev.map((m) =>
                m.id === def.id
                  ? {
                      ...m,
                      isLoading: false,
                      status: overallStatus,
                      criteria: evaluatedCriteria,
                    }
                  : m
              )
            );
          }
        } catch (err) {
          if (generation === generationRef.current) {
            setMetrics((prev) =>
              prev.map((m) =>
                m.id === def.id
                  ? {
                      ...m,
                      isLoading: false,
                      status: ChecklistMetricStatus.BLOCKED,
                      criteria: m.criteria.map((c) => ({
                        ...c,
                        status: ChecklistMetricStatus.BLOCKED,
                      })),
                    }
                  : m
              )
            );
          }
        }
      });
    } catch (err) {
      if (generation === generationRef.current) {
        setError('Failed to initialize metric checking');
        setIsLoading(false);
      }
    }
  }, [handoffId, locationId, queryClient]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    setMetrics,
    isLoading,
    error,
    refresh: fetchMetrics,
  };
};
